import { useEffect, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  listAccounts,
  fetchReport,
  defaultDateRange,
  isoDate,
  GOOGLE_ADS_SCOPE,
  GoogleAdsConfigError,
} from "@agencylens/connector-google-ads";
import { renderGoogleAdsReport } from "@agencylens/pdf";
import { DEFAULT_AGENCY_BRAND } from "@agencylens/shared";
import { Button } from "@/components/ui/Button.js";
import {
  loadConnectionRaw,
  readAccessToken,
  readRefreshToken,
  saveGoogleConnection,
  deleteConnection,
  isExpired,
} from "@/lib/connections.js";
import { connectGoogle, refreshGoogleToken } from "@/lib/oauth.js";
import { svgElementToPng } from "@/lib/chart-rasterize.js";
import { useApp } from "@/store/app.js";
import { config } from "@/lib/config.js";

export function GoogleAdsPanel({
  onError,
  configReady,
}: {
  onError: (msg: string | undefined) => void;
  configReady: boolean;
}) {
  const {
    googleAdsConnection,
    googleAdsAccounts,
    selectedGoogleAdsCustomerId,
    lastGoogleAdsReport,
    setGoogleAdsConnection,
    setGoogleAdsAccounts,
    setSelectedGoogleAdsCustomerId,
    setLastGoogleAdsReport,
  } = useApp();

  const [busy, setBusy] = useState<string | undefined>();
  const [{ startDate, endDate }, setRange] = useState(() => defaultDateRange(30));
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      const rec = await loadConnectionRaw("google-ads", "default");
      if (rec) setGoogleAdsConnection(rec);
    })().catch((e) => onError(String(e)));
  }, [setGoogleAdsConnection, onError]);

  useEffect(() => {
    if (!googleAdsConnection) return;
    (async () => {
      try {
        setBusy("Listing Google Ads accounts…");
        const token = await ensureFreshToken();
        const accounts = await listAccounts({
          accessToken: token,
          proxyBase: config.googleAdsProxyBase,
        });
        setGoogleAdsAccounts(accounts);
        if (!selectedGoogleAdsCustomerId && accounts.length > 0) {
          setSelectedGoogleAdsCustomerId(accounts[0]!.customerId);
        }
      } catch (e) {
        if (e instanceof GoogleAdsConfigError) {
          onError(
            `Google Ads is not provisioned yet on AgencyLens. ${e.hint ?? ""}`,
          );
        } else {
          onError(`Could not list Google Ads accounts: ${String(e)}`);
        }
      } finally {
        setBusy(undefined);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleAdsConnection?.updatedAt]);

  async function ensureFreshToken(): Promise<string> {
    const rec = await loadConnectionRaw("google-ads", "default");
    if (!rec) throw new Error("No Google Ads connection");
    if (!isExpired(rec)) return readAccessToken(rec);
    const refresh = await readRefreshToken(rec);
    if (!refresh) throw new Error("Token expired and no refresh token. Reconnect Google Ads.");
    const refreshed = await refreshGoogleToken(refresh);
    const newRec = await saveGoogleConnection("google-ads", "default", refreshed);
    setGoogleAdsConnection(newRec);
    return readAccessToken(newRec);
  }

  async function onConnect() {
    onError(undefined);
    setBusy("Opening Google sign-in…");
    try {
      const tokens = await connectGoogle({ scope: GOOGLE_ADS_SCOPE });
      const rec = await saveGoogleConnection("google-ads", "default", tokens);
      setGoogleAdsConnection(rec);
    } catch (e) {
      onError(`Google Ads OAuth failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onDisconnect() {
    await deleteConnection("google-ads", "default");
    setGoogleAdsConnection(undefined);
    setGoogleAdsAccounts([]);
    setSelectedGoogleAdsCustomerId(undefined);
    setLastGoogleAdsReport(undefined);
  }

  async function onGenerate() {
    if (!selectedGoogleAdsCustomerId) return;
    const account = googleAdsAccounts.find(
      (a) => a.customerId === selectedGoogleAdsCustomerId,
    );
    if (!account) return;
    onError(undefined);
    setBusy("Fetching Google Ads data…");
    try {
      const token = await ensureFreshToken();
      const report = await fetchReport(
        { accessToken: token, proxyBase: config.googleAdsProxyBase },
        account,
        startDate,
        endDate,
      );
      setLastGoogleAdsReport(report);
    } catch (e) {
      if (e instanceof GoogleAdsConfigError) {
        onError(`Google Ads is not provisioned yet on AgencyLens. ${e.hint ?? ""}`);
      } else {
        onError(`Google Ads fetch failed: ${String(e)}`);
      }
    } finally {
      setBusy(undefined);
    }
  }

  async function onDownloadPdf() {
    if (!lastGoogleAdsReport) return;
    onError(undefined);
    setBusy("Generating PDF…");
    try {
      const svgEl = chartContainerRef.current?.querySelector<SVGSVGElement>(
        "svg.recharts-surface",
      );
      let chartPng: Uint8Array | undefined;
      if (svgEl) {
        try {
          chartPng = await svgElementToPng(svgEl, 2);
        } catch (e) {
          console.warn("Chart rasterize failed; falling back to pdf-lib chart", e);
        }
      }
      const bytes = await renderGoogleAdsReport({
        data: lastGoogleAdsReport,
        agency: DEFAULT_AGENCY_BRAND,
        chartPng,
      });
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agencylens-google-ads-report-${isoDate(new Date())}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      onError(`PDF generation failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  const ccy = lastGoogleAdsReport?.account.currencyCode ?? "USD";
  const ccyPrefix =
    ccy === "USD" || ccy === "CAD" || ccy === "AUD"
      ? "$"
      : ccy === "EUR"
      ? "€"
      : ccy === "GBP"
      ? "£"
      : ccy === "INR"
      ? "₹"
      : "";

  return (
    <>
      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-3 text-lg font-medium">Google Ads</h2>
        {googleAdsConnection ? (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Connected
            </span>
            <span className="text-sm text-slate-500">
              {googleAdsAccounts.length}{" "}
              {googleAdsAccounts.length === 1 ? "account" : "accounts"} · expires{" "}
              {new Date(googleAdsConnection.expiresAt).toLocaleString()}
            </span>
            <Button variant="ghost" size="sm" onClick={onDisconnect} disabled={busy !== undefined}>
              Disconnect
            </Button>
          </div>
        ) : (
          <Button onClick={onConnect} disabled={busy !== undefined || !configReady}>
            Connect Google Ads
          </Button>
        )}
      </section>

      {googleAdsConnection && (
        <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-3 text-lg font-medium">Generate Google Ads report</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Account</span>
              <select
                value={selectedGoogleAdsCustomerId ?? ""}
                onChange={(e) =>
                  setSelectedGoogleAdsCustomerId(e.target.value || undefined)
                }
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              >
                <option value="">— select —</option>
                {googleAdsAccounts.map((a) => (
                  <option key={a.customerId} value={a.customerId}>
                    {a.descriptiveName} ({a.customerId})
                    {a.isManager ? " — manager" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Start date</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setRange({ startDate: e.target.value, endDate })}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">End date</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setRange({ startDate, endDate: e.target.value })}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                disabled={busy !== undefined}
              />
            </label>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button
              onClick={onGenerate}
              disabled={busy !== undefined || !selectedGoogleAdsCustomerId}
            >
              Fetch data
            </Button>
            {busy && <span className="text-sm text-slate-500">{busy}</span>}
          </div>
        </section>
      )}

      {lastGoogleAdsReport && (
        <>
          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
            <div className="mb-4 flex items-end justify-between">
              <div>
                <h2 className="text-lg font-medium">
                  {lastGoogleAdsReport.account.descriptiveName}
                </h2>
                <p className="text-sm text-slate-500">
                  {lastGoogleAdsReport.startDate} → {lastGoogleAdsReport.endDate}
                </p>
              </div>
              <Button onClick={onDownloadPdf} disabled={busy !== undefined}>
                Download Google Ads PDF
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <StatCard
                label="Spend"
                value={`${ccyPrefix}${lastGoogleAdsReport.totals.cost.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
              />
              <StatCard
                label="Clicks"
                value={lastGoogleAdsReport.totals.clicks.toLocaleString("en-US")}
              />
              <StatCard
                label="Conversions"
                value={lastGoogleAdsReport.totals.conversions.toLocaleString("en-US")}
              />
              <StatCard
                label="ROAS"
                value={
                  lastGoogleAdsReport.totals.cost > 0
                    ? `${(
                        lastGoogleAdsReport.totals.conversionValue /
                        lastGoogleAdsReport.totals.cost
                      ).toFixed(2)}×`
                    : "—"
                }
              />
            </div>
          </section>

          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
            <h3 className="mb-3 text-sm font-medium text-slate-700">Spend over time</h3>
            <div ref={chartContainerRef} style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lastGoogleAdsReport.daily}>
                  <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 4" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="cost" stroke="#1d4ed8" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
            <h3 className="mb-3 text-sm font-medium text-slate-700">Top campaigns</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Campaign</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Spend</th>
                    <th className="py-2 pr-4 font-medium">Clicks</th>
                    <th className="py-2 pr-4 font-medium">Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {lastGoogleAdsReport.campaigns.map((c) => (
                    <tr key={c.campaignId} className="border-b border-slate-100">
                      <td className="py-2 pr-4">{c.campaignName}</td>
                      <td className="py-2 pr-4 text-xs text-slate-500">{c.status}</td>
                      <td className="py-2 pr-4">
                        {ccyPrefix}
                        {c.cost.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                      </td>
                      <td className="py-2 pr-4">{c.clicks.toLocaleString("en-US")}</td>
                      <td className="py-2 pr-4">{c.conversions.toLocaleString("en-US")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}
